import { format } from 'date-fns';
import { escape } from 'lodash-es';

/** Strip HTML tags and quotes out of the given string, replacing them with meaningful punctuation. */
export function plainText(html: string): string {
    if (!html) return '';

    const dom = document.createElement('div');
    dom.innerHTML = html;

    dom.querySelectorAll('blockquote, script, img').forEach((el) => el.remove());

    dom.querySelectorAll('p, h1, h2, h3, h4, h5, h6, ul, ol, li, br').forEach((el) => {
        el.insertAdjacentText('afterend', ' ');
    });

    return (dom.textContent || '').replace(/\s+/g, ' ').trim();
}

export function nl2br(str: string): string {
    return str.replace(/\n/g, '<br>');
}

export function markIndices(text: string, indices: [number, number][]): string {
    let html = '';
    let prev = 0;

    for (const [start, end] of indices) {
        html += escape(text.substring(prev, start));
        html += '<mark>' + escape(text.substring(start, end + 1)) + '</mark>';
        prev = end + 1;
    }

    return html + escape(text.substring(prev, text.length));
}

const currencyFormatter = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    // @ts-ignore
    trailingZeroDisplay: 'stripIfInteger',
});

export function formatCurrency(value: number) {
    return currencyFormatter.format(value);
}

const currencyCompactFormatter = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    notation: 'compact',
});

export function formatCurrencyCompact(value: number) {
    return currencyCompactFormatter.format(value);
}

const numberFormatter = new Intl.NumberFormat('en-AU');

export function formatNumber(value: number) {
    return numberFormatter.format(value);
}

const numberCompactFormatter = new Intl.NumberFormat('en-AU', {
    notation: 'compact',
});

export function formatNumberCompact(value: number) {
    return numberCompactFormatter.format(value);
}

const percentageFormatter = new Intl.NumberFormat('en-AU', {
    style: 'percent',
});

export function formatPercentage(value: number) {
    return percentageFormatter.format(value);
}

export function formatNumberFifty(value: number) {
    if (value < 50) {
        return value;
    }
    return formatNumber(Math.floor(value / 50) * 50);
}

export function formatTimer(d: number) {
    const h = Math.floor(d / 3600);
    const m = String(Math.floor((d % 3600) / 60));
    const s = String(Math.floor((d % 3600) % 60)).padStart(2, '0');

    const parts = h ? [h, m.padStart(2, '0'), s] : [m, s];

    return parts.join(':');
}

export function toLocalDatetimeInput(value?: string | null): string {
    if (!value) return '';
    return format(new Date(value), "yyyy-MM-dd'T'HH:mm");
}

export function toIsoDatetime(value: string): string | null {
    return value ? new Date(value).toISOString() : null;
}

const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
});

export function formatDateTime(value: string): string {
    return dateTimeFormatter.format(new Date(value));
}

export function ucfirst(string: string) {
    return string ? string.charAt(0).toUpperCase() + string.slice(1) : string;
}

export function nullableDate<T extends string | null>(
    value: T,
): T extends null ? Date | null : Date {
    return (value ? new Date(value) : null) as T extends null ? Date | null : Date;
}
